import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import { ENGAGEMENT_LABEL, fmtDate } from '../format';
import type { Person } from '../types';
import { Change, ConfirmFlow } from '../components/Confirm';
import { ErrorNote, Loading, toast } from '../components/ui';

/** Stages before the first day: dates can be corrected and the person withdrawn. */
export const PRE_START = ['candidate', 'offered', 'prestart'];

const useRefresh = () => {
  const qc = useQueryClient();
  return () => { for (const k of ['person', 'people', 'accounts', 'overview', 'runs', 'run', 'documents']) void qc.invalidateQueries({ queryKey: [k] }); };
};

// ───────────────────────────── Change dates ─────────────────────────────

interface DatePreview {
  person: string; before: { offer: string | null; start: string | null }; after: { offer: string | null; start: string | null };
  checklistId: number | null; moves: { title: string; from: string | null; to: string | null; automatic: boolean }[];
}

export function ChangeDatesDialog({ person, onClose }: { person: Person; onClose: () => void }) {
  const refresh = useRefresh();
  const [offer, setOffer] = useState(person.offerAcceptedDate ?? '');
  const [start, setStart] = useState(person.startDate ?? '');
  const body = () => ({ offer: offer || null, start: start || null });
  const unchanged = (offer || null) === person.offerAcceptedDate && (start || null) === person.startDate;
  return (
    <ConfirmFlow<DatePreview>
      title={`Change ${person.givenName}'s dates`}
      onClose={onClose}
      canContinue={!unchanged}
      review={async () => {
        const p = await api.send<DatePreview>('POST', `/api/people/${person.id}/dates/preview`, body());
        return (
          <>
            <p>You are about to change <strong>{p.person}</strong>’s key dates:</p>
            <ul className="plain-list">
              <Change label="Offer accepted" from={fmtDate(p.before.offer)} to={fmtDate(p.after.offer)} />
              <Change label="Start date" from={fmtDate(p.before.start)} to={fmtDate(p.after.start)} />
            </ul>
            {p.checklistId ? (p.moves.length ? (
              <>
                <p>{p.moves.length} open checklist task{p.moves.length === 1 ? '' : 's'} will move with {p.moves.length === 1 ? 'it' : 'them'}:</p>
                <ul className="plain-list small">{p.moves.map((m, i) => <li key={i}>{m.automatic && <strong>Automatic · </strong>}{m.title}: <s className="dim">{fmtDate(m.from)}</s> → <strong>{fmtDate(m.to)}</strong></li>)}</ul>
              </>
            ) : <p className="small">No open checklist tasks change date.</p>) : <p className="small">There is no onboarding checklist yet, so only the person record changes.</p>}
          </>
        );
      }}
      confirmLabel="Yes, change the dates"
      onConfirm={() => api.send('POST', `/api/people/${person.id}/dates`, body())}
      onDone={() => { refresh(); toast('Dates changed — checklist moved with them'); }}
    >
      <p className="small">Use this to fix a mistake or when the start date moves. Dates can be changed until the start date.</p>
      <div className="two">
        <label className="field" htmlFor="cd-offer"><span>Offer accepted</span><input id="cd-offer" type="date" className="txt" value={offer} onChange={(e) => setOffer(e.target.value)} /></label>
        <label className="field" htmlFor="cd-start"><span>Start date</span><input id="cd-start" type="date" className="txt" value={start} onChange={(e) => setStart(e.target.value)} /></label>
      </div>
    </ConfirmFlow>
  );
}

// ───────────────────────────── Letter of offer ─────────────────────────────

interface OfferPreview { person: string; sendTo: string; title: string; body: string; account: { upn: string | null; exists: boolean }; replaces: string[]; isWord: boolean; typed: Record<string, string> }
type OfferResult = { documentId: number; account: { upn: string | null; error: string | null }; warning: string | null };

interface OfferTpl { key: string; name: string; kind: string; fields: string[] }
/** Fields the offer dialog itself asks for; anything else the template needs is asked below them. */
const OFFER_FORM_FIELDS = ['position.title', 'engagement.type', 'engagement.fte', 'start.date', 'offer.respondBy', 'offer.conditions'];

export function OfferDialog({ person, onClose }: { person: Person; onClose: () => void }) {
  const refresh = useRefresh();
  const nav = useNavigate();
  const tpls = useQuery({ queryKey: ['offer-templates'], queryFn: () => api.get<OfferTpl[]>('/api/offer-templates') });
  const [tplKey, setTplKey] = useState('');
  const key = tplKey || tpls.data?.[0]?.key || '';
  const tpl = tpls.data?.find((t) => t.key === key);
  const [f, setF] = useState({
    positionTitle: person.positionTitle ?? '', engagementType: person.engagementType, fte: person.fte?.toString() ?? '',
    startDate: person.startDate ?? '', conditions: '', respondBy: '',
  });
  const [extra, setExtra] = useState<Record<string, string>>({});
  const extraInfo = useQuery({
    queryKey: ['template-fields', key, person.id],
    queryFn: () => api.send<{ fields: { key: string; label: string; value: string; typed: boolean }[] }>('POST', `/api/templates/${key}/fields`, { personId: person.id, fields: {} }),
    enabled: !!key,
  });
  const extraFields = (extraInfo.data?.fields ?? []).filter((x) => !OFFER_FORM_FIELDS.includes(x.key) && (x.typed || !x.value));
  const usesConditions = !tpl || tpl.fields.includes('offer.conditions');
  const up = (p: Partial<typeof f>) => setF({ ...f, ...p });
  const body = () => ({ ...f, fte: f.fte ? Number(f.fte) : null, respondBy: f.respondBy || null, templateKey: key || null, fields: extra });
  return (
    <ConfirmFlow<OfferResult>
      title={`Send a letter of offer to ${person.name}`}
      onClose={onClose}
      canContinue={!!key && !!f.positionTitle.trim() && !!f.startDate && (!usesConditions || !!f.conditions.trim()) && extraFields.every((x) => (extra[x.key] ?? '').trim())}
      continueLabel="Preview the letter"
      review={async () => {
        const p = await api.send<OfferPreview>('POST', `/api/people/${person.id}/offer/preview`, body());
        return (
          <>
            <p>When you confirm, PTI People will:</p>
            <ol className="small">
              {p.replaces.length > 0 && <li>Cancel the earlier, unsigned letter of offer <strong>{p.replaces.join(', ')}</strong> (its signing link stops working).</li>}
              <li>Send <strong>{p.title}</strong> for signing — first to the Director, then to {p.person} by secure link at <strong>{p.sendTo}</strong>.</li>
              <li>{p.account.exists ? <>Keep the existing Microsoft account <strong>{p.account.upn}</strong>.</> : <>Create the Microsoft account <strong>{p.account.upn}</strong> (no licence, PTI People only) and email a one-time sign-in pass to {p.sendTo}.</>}</li>
              <li>When {p.person} signs, record the offer as accepted, move them to Pre-start and start the onboarding checklist.</li>
            </ol>
            <details open><summary className="small"><strong>The letter{p.isWord ? ' (wording — the PDF keeps your Word layout)' : ''}</strong></summary><pre className="letter-preview">{p.body}</pre></details>
            {p.isWord && <button type="button" className="btn sm" onClick={() => void api.blob(`/api/templates/${key}/preview`, { personId: person.id, fields: p.typed }).then(api.openBlob).catch((e: Error) => toast(e.message))}>Open as PDF</button>}
          </>
        );
      }}
      confirmLabel="Yes, send the offer and create the account"
      onConfirm={() => api.send<OfferResult>('POST', `/api/people/${person.id}/offer`, body())}
      onDone={(r) => {
        refresh();
        const notes = [r.warning, r.account.error && `The account could not be created yet: ${r.account.error}`].filter(Boolean);
        toast(notes.length ? `Offer saved. ${notes.join(' ')}` : `Offer sent and ${r.account.upn} created`);
        nav(`/documents/${r.documentId}`);
      }}
    >
      <p className="small">This starts {person.givenName}’s lifecycle.</p>
      {(tpls.data?.length ?? 0) > 1 && (
        <label className="field" htmlFor="of-tpl"><span>Letter of offer template</span>
          <select id="of-tpl" value={key} onChange={(e) => { setTplKey(e.target.value); setExtra({}); }}>{tpls.data!.map((t) => <option key={t.key} value={t.key}>{t.name}{t.kind === 'word' ? ' (Word)' : ''}</option>)}</select>
        </label>
      )}
      <label className="field" htmlFor="of-title"><span>Position title</span><input id="of-title" className="txt" required value={f.positionTitle} onChange={(e) => up({ positionTitle: e.target.value })} /></label>
      <div className="two">
        <label className="field" htmlFor="of-eng"><span>Engagement</span>
          <select id="of-eng" value={f.engagementType} onChange={(e) => up({ engagementType: e.target.value })}>{Object.entries(ENGAGEMENT_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        </label>
        <label className="field" htmlFor="of-fte"><span>FTE</span><input id="of-fte" type="number" step="0.1" min="0" max="1" className="txt" value={f.fte} onChange={(e) => up({ fte: e.target.value })} /></label>
      </div>
      <div className="two">
        <label className="field" htmlFor="of-start"><span>Proposed start date</span><input id="of-start" type="date" className="txt" required value={f.startDate} onChange={(e) => up({ startDate: e.target.value })} /></label>
        <label className="field" htmlFor="of-by"><span>Please respond by <span className="dim">(optional)</span></span><input id="of-by" type="date" className="txt" value={f.respondBy} onChange={(e) => up({ respondBy: e.target.value })} /></label>
      </div>
      {usesConditions && (
        <label className="field" htmlFor="of-cond"><span>Conditions and terms <span className="dim">(pay, hours, screening checks — appears in the letter)</span></span>
          <textarea id="of-cond" className="txt" rows={5} value={f.conditions} onChange={(e) => up({ conditions: e.target.value })} />
        </label>
      )}
      {extraFields.length > 0 && <p className="small"><strong>Also needed for this letter</strong> <span className="dim">— details not on {person.givenName}’s record</span></p>}
      {extraFields.map((x) => (
        <label key={x.key} className="field" htmlFor={`of-x-${x.key}`}><span>{x.label}</span><input id={`of-x-${x.key}`} className="txt" value={extra[x.key] ?? ''} onChange={(e) => setExtra({ ...extra, [x.key]: e.target.value })} /></label>
      ))}
    </ConfirmFlow>
  );
}

// ───────────────────────────── Withdraw ─────────────────────────────

interface WithdrawPreview {
  person: string; stage: string; checklists: { id: number; kind: string }[]; documents: { id: number; title: string; status: string }[];
  account: { upn: string | null; status: string } | null;
}

export function WithdrawDialog({ person, onClose }: { person: Person; onClose: () => void }) {
  const refresh = useRefresh();
  const [reason, setReason] = useState('');
  const pv = useQuery({ queryKey: ['withdraw-preview', person.id], queryFn: () => api.get<WithdrawPreview>(`/api/people/${person.id}/withdraw/preview`) });
  const list = (p: WithdrawPreview) => (
    <ul className="plain-list small">
      {p.checklists.map((c) => <li key={c.id}>Cancel the {c.kind} checklist CL-{String(c.id).padStart(4, '0')}</li>)}
      {p.documents.map((d) => <li key={d.id}>Withdraw “{d.title}” ({d.status === 'sent' ? 'out for signing — the signing link stops working' : 'draft'})</li>)}
      {p.account && <li><strong>Delete the Microsoft account {p.account.upn}</strong>{p.account.status === 'active' ? ' (they have already signed in)' : ''}</li>}
      <li>Mark {p.person} as <strong>Withdrawn</strong>. Their record and any signed documents are kept.</li>
    </ul>
  );
  return (
    <ConfirmFlow
      title={`Withdraw ${person.name}`}
      onClose={onClose}
      canContinue={!!pv.data && reason.trim().length >= 3}
      danger
      review={() => (
        <>
          <p><strong>This cannot be undone.</strong> Withdrawing {pv.data!.person} will:</p>
          {list(pv.data!)}
          <p className="small">Reason recorded: “{reason.trim()}”</p>
        </>
      )}
      confirmLabel={`Yes, withdraw ${person.name}`}
      onConfirm={() => api.send('POST', `/api/people/${person.id}/withdraw`, { reason: reason.trim() })}
      onDone={() => { refresh(); toast(`${person.name} has been withdrawn`); }}
    >
      <p className="small">Use this when an offer is declined or falls through before {person.givenName} starts.</p>
      {pv.isLoading ? <Loading /> : pv.error ? <ErrorNote error={pv.error} /> : (<><p>This will:</p>{list(pv.data!)}</>)}
      <label className="field" htmlFor="wd-reason"><span>Reason (recorded in the audit log)</span><input id="wd-reason" className="txt" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Candidate declined the offer" /></label>
    </ConfirmFlow>
  );
}

// ───────────────────────────── Account actions ─────────────────────────────

type Who = { id: string; name: string };

export function CreateAccountDialog({ person, finish, onClose }: { person: Who; finish?: boolean; onClose: () => void }) {
  const refresh = useRefresh();
  return (
    <ConfirmFlow<{ upn: string }>
      title={finish ? `Finish setting up ${person.name}'s account` : `Create ${person.name}'s Microsoft account now`}
      onClose={onClose}
      review={() => <p>Create the Microsoft account for <strong>{person.name}</strong> now, with no licence, in PTI-PreStart and their role group, and email a one-time sign-in pass to their personal email address.</p>}
      confirmLabel="Yes, create the account"
      onConfirm={() => api.send<{ upn: string }>('POST', `/api/accounts/${person.id}/create`)}
      onDone={(r) => { refresh(); toast(`Created ${r.upn} — activation pass emailed`); }}
    >
      <p>Normally this happens automatically when the letter of offer is sent. Use this if that step failed or the person was added another way.</p>
    </ConfirmFlow>
  );
}

export function ResendPassDialog({ person, onClose }: { person: Who; onClose: () => void }) {
  const refresh = useRefresh();
  return (
    <ConfirmFlow
      title={`Send ${person.name} a new sign-in pass`}
      onClose={onClose}
      review={() => <p>Email <strong>{person.name}</strong> a new one-time sign-in pass at their personal email address. Any earlier pass stops working.</p>}
      confirmLabel="Yes, send a new pass"
      onConfirm={() => api.send('POST', `/api/accounts/${person.id}/resend-pass`)}
      onDone={() => { refresh(); toast('New sign-in pass emailed'); }}
    >
      <p>Use this when the first pass expired (they last 7 days) or was lost.</p>
    </ConfirmFlow>
  );
}

export function LockDialog({ person, onClose }: { person: Who; onClose: () => void }) {
  const refresh = useRefresh();
  const [reason, setReason] = useState('');
  return (
    <ConfirmFlow
      title={`Lock ${person.name}'s account now`}
      onClose={onClose}
      danger
      canContinue={reason.trim().length >= 3}
      review={() => (
        <>
          <p><strong>{person.name}</strong> will be signed out everywhere straight away:</p>
          <ul className="plain-list small"><li>Password reset</li><li>Sign-in blocked</li><li>All sessions revoked</li></ul>
          <p className="small">Reason recorded: “{reason.trim()}”. Their mailbox and files stay in place.</p>
        </>
      )}
      confirmLabel={`Yes, lock ${person.name.split(' ')[0]}'s account`}
      onConfirm={() => api.send('POST', `/api/accounts/${person.id}/lock`, { reason: reason.trim() })}
      onDone={() => { refresh(); toast(`${person.name}’s account is locked`); }}
    >
      <p>This resets the password, blocks sign-in and signs them out everywhere.</p>
      <label className="field" htmlFor="lk-reason"><span>Reason (recorded in the audit log)</span><input id="lk-reason" className="txt" value={reason} onChange={(e) => setReason(e.target.value)} /></label>
    </ConfirmFlow>
  );
}
